import type { CompanyStatus } from './types.ts';

/**
 * Human-readable labels for each company status. Single source of truth shared
 * by the status badge and the admin filters so the two can never drift apart.
 */
export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  review: 'Action required',
  changes: 'Vendor change requested',
  compliant: 'Compliant',
  expired: 'Not Compliant',
  deactivated: 'Deactivated',
};

/**
 * Vendor-facing overrides for statuses whose meaning flips by audience. The
 * status is stored as a single value, but "whose turn is it" reads opposite to
 * the two sides:
 *   - `review`  = vendor submitted, the ball is with the admin. The admin sees
 *     "Action required"; to the vendor it is simply "Under Review by Admin".
 *   - `changes` = admin returned it, the ball is back with the vendor. The admin
 *     sees "Vendor change requested"; to the vendor that IS their action item.
 * Statuses not listed here fall back to COMPANY_STATUS_LABELS, so the shared
 * wording stays a single source of truth.
 */
const VENDOR_STATUS_LABEL_OVERRIDES: Partial<Record<CompanyStatus, string>> = {
  review: 'Under Review by Admin',
  changes: 'Action required',
};

/**
 * Human-readable status label as the given audience should read it. `audience`
 * defaults to 'admin' (the canonical labels); 'vendor' covers any party being
 * reviewed (vendors and drivers in their own portals).
 */
export function companyStatusLabel(
  status: CompanyStatus,
  audience: 'admin' | 'vendor' = 'admin',
): string {
  if (audience === 'vendor') {
    return VENDOR_STATUS_LABEL_OVERRIDES[status] ?? COMPANY_STATUS_LABELS[status];
  }
  return COMPANY_STATUS_LABELS[status];
}

/** Statuses in lifecycle order — drives the order of filter dropdown options. */
export const COMPANY_STATUSES: CompanyStatus[] = [
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
  'deactivated',
];
